import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { Lock, Eye, EyeOff } from "lucide-react";
import OrgAuthShell, { Champ, BoutonPrincipal } from "./OrgAuthShell";

const API = "https://api.komorotel.com/api";

/* Le mot de passe posé à la création de la demande est un jeton aléatoire
   que personne ne connaît. Ce formulaire est le seul moyen d'entrer. */
export default function OrganizationActivate() {
  const { token } = useParams();
  const navigate  = useNavigate();

  const [info, setInfo]   = useState(null);
  const [etat, setEtat]   = useState("chargement");
  const [erreur, setErreur] = useState("");
  const [mdp, setMdp]         = useState("");
  const [confirme, setConfirme] = useState("");
  const [voir, setVoir]       = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/organization/activate/${token}`)
      .then(r => { setInfo(r.data); setEtat("pret"); })
      .catch(err => {
        setErreur(err.response?.data?.message || "Ce lien n'est plus valable.");
        setEtat("erreur");
      });
  }, [token]);

  const envoyer = async (e) => {
    e.preventDefault();
    if (mdp.length < 8)   return toast.error("8 caractères minimum.");
    if (mdp !== confirme) return toast.error("Les deux mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/organization/activate/${token}`, { password: mdp });
      toast.success(r.data.message);
      navigate(r.data.redirect || "/organisation/connexion");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    } finally {
      setLoading(false);
    }
  };

  if (etat === "chargement") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#fff" }}>
        <div
          className="w-8 h-8 rounded-full animate-spin"
          style={{ border: "3px solid #e5e7eb", borderTopColor: "var(--k-green)" }}
        />
      </div>
    );
  }

  if (etat === "erreur") {
    return (
      <OrgAuthShell
        titre="Lien inutilisable"
        sousTitre={erreur}
        pied={
          <>
            Demandez un nouveau lien à Komorotel, ou{" "}
            <a href="/organisation/connexion" className="font-semibold text-[var(--k-green)] hover:underline">
              connectez-vous
            </a>{" "}
            si votre compte est déjà actif.
          </>
        }
      >
        <span />
      </OrgAuthShell>
    );
  }

  const activation = info.purpose !== "org_reset";

  return (
    <OrgAuthShell
      titre={activation ? "Choisissez votre mot de passe" : "Nouveau mot de passe"}
      sousTitre={
        activation
          ? `Le compte de ${info.organizationName} est validé. Ce mot de passe vous servira à ouvrir votre espace.`
          : `Pour votre accès à l'espace de ${info.organizationName}.`
      }
    >
      <form onSubmit={envoyer} className="flex flex-col gap-5">
        <p className="text-sm text-gray-500">{info.email}</p>

        <Champ
          label="Mot de passe"
          type={voir ? "text" : "password"}
          value={mdp}
          required
          onChange={setMdp}
          aide="8 caractères minimum."
          icone={<Lock size={17} strokeWidth={1.75} />}
          droite={
            <button
              type="button"
              onClick={() => setVoir(v => !v)}
              aria-label={voir ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              {voir ? <EyeOff size={17} strokeWidth={1.75} /> : <Eye size={17} strokeWidth={1.75} />}
            </button>
          }
        />

        <Champ
          label="Confirmation"
          type={voir ? "text" : "password"}
          value={confirme}
          required
          icone={<Lock size={17} strokeWidth={1.75} />}
          onChange={setConfirme}
        />

        <BoutonPrincipal type="submit" disabled={loading}>
          {loading ? "Enregistrement..." : activation ? "Activer mon compte" : "Changer le mot de passe"}
        </BoutonPrincipal>
      </form>
    </OrgAuthShell>
  );
}
